import {
  Component,
  computed,
  effect,
  inject,
  signal,
  WritableSignal,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TabBarComponent, PageBodyComponent } from '@custom-components';
import { PageHeaderComponent, PageHeaderBadge } from '@custom-components';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { APP_PATHS, DEVICE_DETAIL_TABS, DEVICE_FEEDS_TAB, XEMEX_OID } from '@core/constants';
import { resolveDeviceStates } from '@core/helpers/device-state.helper';
import {
  canViewInstallations,
  installationAccessKnown,
} from '@core/helpers/installation-access.helper';
import { InstallationService, RecentlyVisitedService } from '@core/services';
import { DeviceGeneralTabComponent } from '../../components/device-general-tab/device-general-tab.component';
import { DeviceConfigurationTabComponent } from '../../components/device-configuration-tab/device-configuration-tab.component';
import { DeviceTelemetryTabComponent } from '../../components/device-telemetry-tab/device-telemetry-tab.component';
import { DeviceFeedsTabComponent } from '../../components/device-feeds-tab/device-feeds-tab.component';
import {
  ChannelUpdatePayload,
  ChannelUsage,
  ConnectionStateEnum,
  DeviceModel,
  effectiveMeterRole,
  FirmwareModel,
  getUserActivePartner,
  getUserFeaturePermissions,
  getUserRole,
  InstallationStateEnum,
  LocationModel,
  METER_ROLE_ICONS,
  PartnerModel,
  PollingDeviceEnum,
} from '@core/models';
import { PHASE_COUNT_EDITOR_ROLES } from '../../constants/phase-count-editor-roles.constant';
import { PARTNER_ADMIN_USER_ROLES } from '../../constants/device-role-groups.constant';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceStore } from '@root/app/store/device.store';
import { ConnectionTestDialogComponent } from '../../components/connection-test-dialog/connection-test-dialog.component';
import { DeviceCsvExportDialogComponent } from '../../components/device-csv-export-dialog/device-csv-export-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UserStore } from '@root/app/store/user.store';
import { AuthStore } from '@root/app/store/auth.store';
import { TopologicalDeviceTelemetryStore } from '@root/app/store/topological-device-telemetry.store';
import { FirmwareUpdateDialogComponent } from '../../../firmware-management/components/firmware-update-dialog/firmware-update-dialog.component';
import { DeviceReadoutDialogComponent } from '../../components/device-readout-dialog/device-readout-dialog.component';
import { Store } from '@ngrx/store';
import { FirmwareActions, selectFirmwareItems } from '@root/app/store/firmware.index';
import {
  FirmwareCatalogueState,
  resolveFirmwareCatalogueState,
  resolveFirmwareTransport,
} from '@core/utils/firmware-catalogue.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DeviceActivityTabComponent,
  VERIFICATION_ACTION,
} from '../../components/device-activity-tab/device-activity-tab.component';
import { DeviceAlarmsTabComponent } from '../../components/device-alarms-tab/device-alarms-tab.component';
import { DeviceSettingsTabComponent } from '../../components/device-settings-tab/device-settings-tab.component';
import { METER_ROLE_EDITOR_ROLES } from '../../constants/meter-role-editor-roles.constant';
import { ActivityStore } from '@root/app/store/activity.store';
import { ActivityFilters, ActivityResponse } from '@core/models/activity.model';

/** Header badge per device state (RDT-1565): Online/Offline for an installed device, otherwise
 * its installation state. */
const DEVICE_STATE_BADGES: Record<
  string,
  Pick<PageHeaderBadge, 'status' | 'icon'> & { labelKey: string }
> = {
  [ConnectionStateEnum.Online]: {
    labelKey: 'devices.state.online',
    status: 'success',
    icon: 'wifi',
  },
  [ConnectionStateEnum.Offline]: {
    labelKey: 'devices.state.offline',
    status: 'error',
    icon: 'wifi_off',
  },
  [InstallationStateEnum.Installed]: {
    labelKey: 'devices.state.installed',
    status: 'inactive',
    icon: 'check_circle',
  },
  [InstallationStateEnum.ToBeInstalled]: {
    labelKey: 'devices.state.toBeInstalled',
    status: 'inactive',
    icon: 'schedule',
  },
  [InstallationStateEnum.InstallationFailed]: {
    labelKey: 'devices.state.installationFailed',
    status: 'warning',
    icon: 'error_outline',
  },
};

@Component({
  selector: 'xemex-device-detail',
  imports: [
    MatButtonModule,
    RouterModule,
    TranslateModule,
    PageHeaderComponent,
    DeviceGeneralTabComponent,
    DeviceConfigurationTabComponent,
    DeviceTelemetryTabComponent,
    DeviceFeedsTabComponent,
    DeviceActivityTabComponent,
    DeviceAlarmsTabComponent,
    DeviceSettingsTabComponent,
    TabBarComponent,
    PageBodyComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './device-detail.component.html',
})
export class DeviceDetailComponent {
  readonly deviceStore = inject(DeviceStore);
  readonly topologyTelemetryStore = inject(TopologicalDeviceTelemetryStore);
  readonly dialog = inject(MatDialog);
  readonly authStore = inject(AuthStore);
  readonly userStore = inject(UserStore);
  readonly store = inject(Store);
  readonly activityStore = inject(ActivityStore);
  private readonly translate = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly recentlyVisited = inject(RecentlyVisitedService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly installationService = inject(InstallationService);
  private recentlyVisitedRecordedDeviceId: string | undefined;
  deviceId: any;
  deviceDetail: DeviceModel | undefined;
  partnerRes: PartnerModel | undefined;
  locationRes: LocationModel | undefined;
  activityRes: ActivityResponse | undefined;
  availableFirmwares: FirmwareModel[] = [];
  private firmwareLoaded = false;

  APP_PATHS = APP_PATHS;
  selectedTab = 'general';

  private loadedTabs = {
    general: false,
    configuration: false,
    insights: false,
    activity: false,
    alarms: false,
    'alarm-settings': false,
  };
  shouldLoadTelemetry: WritableSignal<boolean> = signal(false);
  isPerformingReadout = false;
  private installationGroupsLoaded = false;
  isRefreshingSim = signal(false);
  isTopUpInProgress = signal(false);
  isExtendInProgress = signal(false);

  readonly showInstallations = computed(() =>
    canViewInstallations(this.userStore.user(), this.authStore.permissions()),
  );
  readonly channelUsage = signal<ChannelUsage | null>(null);
  readonly isLoadingChannelUsage = signal(false);
  private channelUsageRequested = false;
  private timeZoneRequestedFor: string | null = null;
  private readonly tabsWithFeeds = [...DEVICE_DETAIL_TABS, DEVICE_FEEDS_TAB];

  constructor() {
    const params = this.route.snapshot.params;
    this.deviceId = params['deviceId'];
    this.deviceStore.getDeviceDetailMethod(this.deviceId);
    this.loadedTabs.general = true;

    if (params['tab']) {
      this.loadTabData(params['tab']);
      this.selectedTab = params['tab'];
    }
    effect(() => {
      this.partnerRes = this.deviceStore.partnerByDeviceId();
      this.locationRes = this.deviceStore.locationByDeviceId();
      this.activityRes = this.activityStore.activityRes();
    });
    effect(() => {
      this.deviceDetail = this.deviceStore.deviceDetail();
      if (this.shouldLoadTelemetry() && this.deviceDetail) {
        this.loadTelemetry();
        this.shouldLoadTelemetry.update(_ => false);
      }
      if (this.deviceDetail && this.recentlyVisitedRecordedDeviceId !== this.deviceId) {
        this.recentlyVisitedRecordedDeviceId = this.deviceId;
        this.recentlyVisited.record({
          type: 'device',
          id: this.deviceId,
          icon: 'router',
          primary: this.deviceDetail.deviceLDN,
          secondary: this.deviceDetail.deviceType,
          route: ['/', APP_PATHS.Devices, this.deviceId],
        });
      }
    });

    effect(() => {
      const hasFirmwareAccess = this.permissions().includes('firmware_manager');
      if (hasFirmwareAccess && !this.firmwareLoaded) {
        this.store.dispatch(FirmwareActions.getFirmwareVersions({ filter: { isActive: true } }));
        this.firmwareLoaded = true;
      }
    });

    // The insights show the device's days in the zone of the installation it feeds, for every user,
    // not only those the Installations pages are for. A lookup the API refuses keeps the fallback zone.
    effect(() => {
      const ldn = this.deviceStore.deviceDetail()?.deviceLDN;
      if (!ldn || this.timeZoneRequestedFor === ldn) return;
      this.timeZoneRequestedFor = ldn;
      this.topologyTelemetryStore.loadTimeZone(ldn);
    });

    // The feeds tab and the general tab's installation list read the same response.
    effect(() => {
      if (this.showInstallations()) {
        this.loadChannelUsage();
      }
    });

    // A link to the Feeds tab can reach a user who may not open it, whose page body would stay
    // empty. Once their profile says so, they land on General instead.
    effect(() => {
      const user = this.userStore.user();
      const permissions = this.authStore.permissions();
      if (
        this.selectedTab === DEVICE_FEEDS_TAB.route &&
        installationAccessKnown(user, permissions) &&
        !canViewInstallations(user, permissions)
      ) {
        this.selectedTab = 'general';
        void this.router.navigate(['/', APP_PATHS.Devices, this.deviceId, 'general'], {
          replaceUrl: true,
        });
      }
    });

    // Load the device's installation groups once permissions resolve (general tab is shown by default).
    effect(() => {
      if (
        this.deviceId &&
        this.permissions().includes('view_installation_groups') &&
        !this.installationGroupsLoaded
      ) {
        this.deviceStore.getInstallationGroupsByDeviceId(Number(this.deviceId));
        this.installationGroupsLoaded = true;
      }
    });

    this.store
      .select(selectFirmwareItems)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(items => {
        if (this.permissions().includes('firmware_manager')) {
          this.availableFirmwares = items;
        }
      });
  }

  permissions(): string[] {
    return getUserFeaturePermissions(
      this.userStore.user(),
      this.authStore.permissions(),
      XEMEX_OID,
    );
  }

  get isXemexUser(): boolean {
    return getUserActivePartner(this.userStore.user())?.partnerOid === XEMEX_OID;
  }

  /** Whether the user may see the installation groups card. */
  get canViewInstallationGroups(): boolean {
    return this.permissions().includes('view_installation_groups');
  }

  /** Whether the user's role may edit this device's phase-count parameter (Settings tab). */
  get canEditPhaseCount(): boolean {
    const roleName = getUserRole(this.userStore.user())?.roleName;
    return !!roleName && PHASE_COUNT_EDITOR_ROLES.includes(roleName);
  }

  /** Partner Admin and Partner User may still override a device-type-hardcoded (e.g. 1-phase
   * KWHIQ) phase count — every other editor role (Mechanic, Xemex staff) respects that lock. */
  get canOverridePhaseCountLock(): boolean {
    const roleName = getUserRole(this.userStore.user())?.roleName;
    return !!roleName && PARTNER_ADMIN_USER_ROLES.includes(roleName);
  }

  /** Whether the user's role may edit this device's meter role (Settings tab). */
  get canEditMeterRole(): boolean {
    const roleName = getUserRole(this.userStore.user())?.roleName;
    return !!roleName && METER_ROLE_EDITOR_ROLES.includes(roleName);
  }

  private get firmwareCatalogueState(): FirmwareCatalogueState | undefined {
    if (!this.deviceDetail) return undefined;
    return resolveFirmwareCatalogueState(
      this.availableFirmwares,
      this.deviceDetail.deviceTypeId,
      this.deviceDetail.deviceFirmwareVersion,
    );
  }

  /** Permission alone is not enough: without an active catalogue row for this device's type the
   * Update action opens nothing, which is how it used to render dead on CompactIQ pages. */
  get canUpdateFirmware(): boolean {
    return (
      this.permissions().includes('firmware_manager') &&
      !!this.firmwareCatalogueState?.offered.length
    );
  }

  /** Whether the user may export data and this device has ever produced telemetry to export. */
  get canExportData(): boolean {
    return (
      this.permissions().includes('export_device_data') &&
      !!this.deviceDetail &&
      this.deviceDetail.devicePollingStatus !== PollingDeviceEnum.NeverReceived
    );
  }

  get breadcrumbs(): BreadcrumbItem[] {
    return [
      { label: this.translate.instant('sidebarMenu.devices'), routerLink: '/' + APP_PATHS.Devices },
      { label: this.deviceDetail?.deviceLDN ?? '—' },
    ];
  }

  get deviceBadges(): PageHeaderBadge[] {
    if (!this.deviceDetail) return [];
    const badges: PageHeaderBadge[] = [];

    // RDT-1565: an installed device shows its connection, any other device its installation
    // state; the portal derives nothing beyond the old-field fallback (D469, D473).
    const { installationState, connectionState } = resolveDeviceStates(this.deviceDetail);
    const state =
      installationState === InstallationStateEnum.Installed
        ? (connectionState ?? installationState)
        : installationState;
    const badge = state ? DEVICE_STATE_BADGES[state] : undefined;
    if (badge) {
      badges.push({
        label: this.translate.instant(badge.labelKey),
        status: badge.status,
        icon: badge.icon,
      });
    }
    const meterRole = effectiveMeterRole(
      this.deviceDetail.meterRole,
      this.partnerRes?.partnerDefaultMeterRole,
    );
    badges.push({
      label: this.translate.instant(`meterRoles.${meterRole}`),
      status: 'inactive',
      icon: METER_ROLE_ICONS[meterRole],
    });
    return badges;
  }

  get availableDeviceTabs() {
    return this.showInstallations() ? this.tabsWithFeeds : DEVICE_DETAIL_TABS;
  }

  onCompanyClicked(partnerOid: string) {
    this.router.navigate(['/', APP_PATHS.Partners, partnerOid]);
  }

  onConnectionTestClicked() {
    this.deviceStore.executeConnectionTest();
    this.dialog.open(ConnectionTestDialogComponent, {
      width: '550px',
      maxWidth: '550px',
      height: '40vh',
      data: {
        connectionTestResult: this.deviceStore.connectionTestResult,
        isExecutingConnectionTest: this.deviceStore.isExecutingConnectionTest,
      },
    });
  }

  onFirmwareUpdateClicked() {
    if (!this.deviceDetail) return;

    const catalogueState = this.firmwareCatalogueState;
    if (!catalogueState?.offered.length) return;

    this.dialog
      .open(FirmwareUpdateDialogComponent, {
        width: '500px',
        data: {
          device: this.deviceDetail,
          transport: resolveFirmwareTransport(this.deviceDetail),
          availableFirmwares: catalogueState.offered,
          installedNotInCatalogue: catalogueState.installedNotInCatalogue,
          currentVersion: this.deviceDetail.deviceFirmwareVersion,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.store.dispatch(FirmwareActions.resetUpdateState());
      });
  }

  onExportCsv() {
    if (!this.deviceDetail) return;

    this.dialog.open(DeviceCsvExportDialogComponent, {
      width: '420px',
      maxWidth: '420px',
      data: {
        deviceId: String(this.deviceDetail.deviceId),
        ldn: this.deviceDetail.deviceLDN,
      },
    });
  }

  onLocationClicked(locationId: number) {
    this.router.navigate(['/', APP_PATHS.Locations, locationId]);
  }

  onInstallationGroupClicked(groupOid: string) {
    this.router.navigate(['/', APP_PATHS.InstallationGroups, groupOid]);
  }

  onChannelChanged(payload: ChannelUpdatePayload) {
    if (payload.settings) {
      this.deviceStore.updateChannelSettingMethod(payload);
    } else if (payload.deviceConfigurationUpdate) {
      this.deviceStore.sendDeviceConfigurationFileToDeviceMethod(payload.deviceConfigurationUpdate);
    }
  }

  onVerificationPageChange($event: { pageIndex: number; pageSize: number }) {
    this.deviceStore.getVerificationAttempts({
      deviceId: this.deviceId,
      pageIndex: $event.pageIndex,
      pageSize: $event.pageSize,
    });
  }

  /** Deep link into the activity tab, e.g. `?action=verification` — onViewAllVerifications()
   * navigates here, and reloading that URL keeps the filter applied. */
  protected activityActionFilter: string | undefined =
    this.route.snapshot.queryParamMap.get('action') ?? undefined;

  private currentActivityFilters: ActivityFilters = this.activityActionFilter
    ? { action: this.activityActionFilter }
    : {};

  /** The verification card's "View all" (configuration tab) shows only the last 3 attempts; the
   * full history lives in the activity log as `verification` activities. Switching tabs is done
   * by hand rather than left to the router: only the `:tab` param changes, so Angular reuses this
   * component and the constructor — which is where `selectedTab` is read from the route — never
   * runs again. The navigate() call is still needed to move the URL and the tab bar along. */
  onViewAllVerifications(): void {
    this.activityActionFilter = VERIFICATION_ACTION;
    this.currentActivityFilters = { action: VERIFICATION_ACTION };
    this.selectedTab = 'activity';
    // The filtered request below is this tab's load, so a later click on the Activity tab in the
    // tab bar doesn't re-issue one on top of it.
    this.loadedTabs.activity = true;
    this.activityStore.getActivities({
      deviceId: this.deviceId,
      ...this.currentActivityFilters,
    });
    this.router.navigate(['/', APP_PATHS.Devices, this.deviceId, 'activity'], {
      queryParams: { action: VERIFICATION_ACTION },
    });
  }

  onActivityFilterChange(filters: ActivityFilters): void {
    this.currentActivityFilters = filters;
    // The activity tab is destroyed and rebuilt on every tab switch, so the action it is seeded
    // with has to follow what the user last applied — otherwise clearing the verification chip
    // and returning to the tab would bring it straight back.
    this.activityActionFilter = filters.action;
    this.activityStore.getActivities({ deviceId: this.deviceId, ...filters });
  }

  onActivityPageChange($event: { pageIndex: number; pageSize: number }) {
    this.activityStore.getActivities({
      deviceId: this.deviceId,
      offset: $event.pageIndex,
      limit: $event.pageSize,
      ...this.currentActivityFilters,
    });
  }

  onTabChange(tabRoute: string) {
    this.selectedTab = tabRoute;
    this.loadTabData(tabRoute);
  }
  private loadTabData(tab: string) {
    if (this.loadedTabs[tab as keyof typeof this.loadedTabs]) {
      return;
    }

    switch (tab) {
      case 'insights':
        this.shouldLoadTelemetry.update(_ => true);
        break;
      case 'configuration':
        this.deviceStore.getChannelConfigurationMethod(this.deviceId);
        if (this.permissions().includes('edit_topology_device')) {
          this.deviceStore.getVerificationAttempts({
            deviceId: this.deviceId,
            pageIndex: undefined,
            pageSize: undefined,
          });
        }
        break;
      case 'activity': {
        this.activityStore.getActivities({
          deviceId: this.deviceId,
          ...this.currentActivityFilters,
        });
        break;
      }
      case 'alarms':
        break;
      case 'alarm-settings':
        break;

      case 'general':
        break;
    }

    this.loadedTabs[tab as keyof typeof this.loadedTabs] = true;
  }
  private loadTelemetry() {
    this.topologyTelemetryStore.getDeviceTopologicalTelemetry({
      deviceId: this.deviceId,
      chartRange: this.topologyTelemetryStore.deviceChartTimeOption(),
      selectedDate: this.topologyTelemetryStore.selectedDate(),
    });
  }

  private loadChannelUsage(): void {
    if (this.channelUsageRequested) return;
    this.channelUsageRequested = true;
    this.isLoadingChannelUsage.set(true);
    this.installationService
      .channelUsage(this.deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: usage => {
          this.channelUsage.set(usage);
          this.isLoadingChannelUsage.set(false);
        },
        error: () => this.isLoadingChannelUsage.set(false),
      });
  }

  performOnDemandReadout() {
    this.isPerformingReadout = true;
    this.deviceStore
      .performReadout(this.deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.isPerformingReadout = false;
          this.dialog.open(DeviceReadoutDialogComponent, {
            width: '640px',
            maxWidth: '95vw',
            data: {
              polId: result.polId,
              deviceId: this.deviceId,
              error: null,
            },
          });
        },
        error: err => {
          this.isPerformingReadout = false;
          this.dialog.open(DeviceReadoutDialogComponent, {
            width: '640px',
            maxWidth: '95vw',
            data: {
              polId: '',
              deviceId: this.deviceId,
              error: JSON.stringify(err),
            },
          });
        },
      });
  }

  onAlarmClosed(): void {
    // Reload device detail to refresh alarm status after closing an alarm
    this.deviceStore.getDeviceDetailMethod(this.deviceId);
  }

  onRefreshSimClicked(): void {
    this.isRefreshingSim.set(true);
    this.deviceStore
      .refreshSim(this.deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.isRefreshingSim.set(false);
          this.deviceStore.setDeviceSimStatus(result);
          this.showSimActionResult('devices.action.refreshSimSuccess');
        },
        error: (error: unknown) => {
          this.isRefreshingSim.set(false);
          // ApiService.formatErrors discards the HttpErrorResponse and keeps only the parsed
          // body, so the backend flags "there is no SIM information to fetch" with a code in the
          // body rather than by status — retrying that will never help, so say so.
          const code = (error as { detail?: { code?: string } })?.detail?.code;
          this.showSimActionResult(
            code === 'no_sim_information'
              ? 'devices.action.refreshSimNoInfo'
              : 'devices.action.refreshSimError',
          );
        },
      });
  }

  onTopUpSimConfirmed(): void {
    const iccid = this.deviceDetail?.simIccid;
    if (!iccid) return;

    this.isTopUpInProgress.set(true);
    this.deviceStore
      .topUpSim(this.deviceId, iccid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.finishSimAction('topUpSim', true),
        error: () => this.finishSimAction('topUpSim', false),
      });
  }

  onExtendSimConfirmed(): void {
    const iccid = this.deviceDetail?.simIccid;
    if (!iccid) return;

    this.isExtendInProgress.set(true);
    this.deviceStore
      .extendSim(this.deviceId, iccid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.finishSimAction('extendSim', true),
        error: () => this.finishSimAction('extendSim', false),
      });
  }

  /** SimActionResult doesn't carry the SIM's full updated status — re-sync via refreshSim right
   * after a successful top-up/extend so the general tab reflects the real outcome, per plan. */
  private finishSimAction(action: 'topUpSim' | 'extendSim', success: boolean): void {
    this.isTopUpInProgress.set(false);
    this.isExtendInProgress.set(false);
    this.showSimActionResult(`devices.action.${action}${success ? 'Success' : 'Error'}`);
    if (success) {
      this.onRefreshSimClicked();
    }
  }

  private showSimActionResult(messageKey: string): void {
    this.snackBar.open(this.translate.instant(messageKey), '', {
      horizontalPosition: 'right',
      verticalPosition: 'top',
      duration: 3000,
    });
  }
}
